import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { ZoidAppService } from '@core/services/zoid-app.service';
import { LiveChatStateService } from '../../../modules/live-chat/services/live-chat-state.service';
import {
  PhoneStatus,
  StatusOptions,
} from '../../../modules/live-chat/interfaces/phone-status.interface';
import { CallStatus } from '@core/enums/call-status.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from '@core/services/logger.service';
import { LogEvent } from '@core/enums/log-event';
import { ModalService } from 'asksuite-citrus';
import { PhoneStatusConfirmationModalComponent } from '../phone-status-confirmation-modal/phone-status-confirmation-modal.component';
import { BroadcastMediatorService } from '@core/services/broadcast-mediator.service';
import { distinctUntilChanged, filter, map, pairwise } from 'rxjs';
import {
  PhoneStateSyncService,
  PhoneToggleStatus,
  OtherTabPhoneState,
} from '@core/services/phone-state-sync.service';
import { BrowserCompatibilityService } from '@core/services/browser-compatibility.service';

@Component({
  selector: 'app-phone-toggle',
  templateUrl: './phone-toggle.component.html',
  styleUrls: ['./phone-toggle.component.scss'],
})
export class PhoneToggleComponent implements OnInit, OnDestroy {
  protected currentStatus: PhoneStatus = {
    description: StatusOptions.OFFLINE,
    color: '#D73930', // TODO: Verificar se vai precisar mudar isso no Dark Mode
    needGoodConnection: false,
    verifyDuplicatedExtension: false,
  };

  protected currentPhoneStatus: PhoneToggleStatus = 'offline';
  protected isLoading = false;
  protected buttonLabel = '';
  protected currentCallStatus: CallStatus = CallStatus.CLOSED;
  protected isRinging = false;
  protected isOtherTabActive = false;
  protected otherTabState: OtherTabPhoneState | null = null;

  private destroyRef = inject(DestroyRef);
  private messageListener?: (event: MessageEvent) => void;

  constructor(
    private zoidAppService: ZoidAppService,
    private liveChatStateService: LiveChatStateService,
    private modalService: ModalService,
    private phoneStateSyncService: PhoneStateSyncService,
    private loggerService: LoggerService,
    private browserCompatibilityService: BrowserCompatibilityService,
  ) {}

  ngOnInit(): void {
    this.resolveButtonLabel();
    this.setupPhoneStateSyncListener();
    this.setupPostMessageListener();

    this.liveChatStateService.currentPhoneAppStatus$
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged((a, b) => a?.description === b?.description), pairwise())
      .subscribe(([statusOld, statusNew]) => {
        if (!statusNew || this.currentPhoneStatus === 'ringing') return;

        this.resolveButtonStatus(statusNew);
        this.phoneStateSyncService.updateStatus(this.currentPhoneStatus, !statusOld);
      });
    this.liveChatStateService.currentPhoneCallStatus$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        pairwise(),
        filter(([last, curr]) => {
          const lockedTransition =
            last === 'CALL_STOP_RINGTONE' && curr === 'CALL_TIMER' ||
            last === 'TRANSFER_REJECTED' && curr === 'CALL_TIMER';
          return !lockedTransition;
        }),
        map(([last, curr]) => curr),
      )
      .subscribe((status) => {
        if (!status) {
          const currentAppStatus = this.liveChatStateService.currentPhoneAppStatus;
          if (currentAppStatus) {
            this.currentPhoneStatus = this.statusResolver(currentAppStatus.description);
          } else {
            this.currentPhoneStatus = 'offline';
          }
          this.resolveButtonLabel();
          this.phoneStateSyncService.updateStatus(this.currentPhoneStatus, true);
          return;
        }

        this.currentPhoneStatus = this.statusResolver(status);
        this.resolveButtonLabel();
        this.phoneStateSyncService.updateStatus(this.currentPhoneStatus);
      });
  }

  ngOnDestroy(): void {
    this.removePostMessageListener();
  }

  onPhoneClick() {
    if (!this.browserCompatibilityService.isRecommendedBrowser()) {
      this.showBrowserWarningModal();
      return;
    }

    this.openPhoneApp();
  }

  private showBrowserWarningModal(): void {
    BroadcastMediatorService.notify('livechat', {
      action: 'showBrowserWarning'
    });
  }

  private openPhoneApp(): void {
    this.zoidAppService.setBarVisible(true);
    requestAnimationFrame(() => {
      this.zoidAppService.openApp({ appName: 'phoneApp' }).then();
    });
  }

  private resolveButtonStatus(status: PhoneStatus) {
    if (status.description === StatusOptions.SOFTPHONE_TRY_CONNECTION) {
      this.currentPhoneStatus = 'loading';
      this.isLoading = true;
      return;
    }
    if (this.isLoading) this.isLoading = false;
    this.currentPhoneStatus = this.statusResolver(status.description);
    this.resolveButtonLabel();
  }

  private statusResolver(status: StatusOptions | CallStatus) {
    const mapper = new Map<StatusOptions | CallStatus, PhoneToggleStatus>([
      [StatusOptions.ONLINE, 'online'],
      [StatusOptions.ON_PAUSE, 'pause'],
      [StatusOptions.PAUSE, 'pause'],
      [StatusOptions.OFFLINE, 'offline'],
      [StatusOptions.SOFTPHONE_CONNECTION_FAILED, 'offline'],
      [StatusOptions.SOFTPHONE_CONNECTION_REJECTED, 'offline'],
      [CallStatus.RECEIVING_CALL, 'ringing'],
      [CallStatus.RECEIVING_TRANSFER, 'ringing'],
      [CallStatus.REFUSED, 'online'],
      [CallStatus.CLOSED, 'online'],
      [CallStatus.DISCONNECTED, 'online'],
      [CallStatus.ON_CALL, 'talking'],
      [CallStatus.CALL_TIMER, 'talking'],
    ]);
    return mapper.get(status) || this.currentPhoneStatus;
  }

  resolveButtonLabel() {
    this.buttonLabel = `WEBPHONE.${this.currentPhoneStatus.toLocaleUpperCase()}`;
  }

  private setupPhoneStateSyncListener(): void {
    this.phoneStateSyncService.otherTabState
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.evaluateOtherTabStatus(state);
      });
  }

  private evaluateOtherTabStatus(data: OtherTabPhoneState | null): void {
    const currentAppStatus = this.liveChatStateService.currentPhoneAppStatus;
    const isCurrentTabOffline = !currentAppStatus ||
      currentAppStatus.description === StatusOptions.OFFLINE ||
      currentAppStatus.description === StatusOptions.SOFTPHONE_CONNECTION_FAILED ||
      currentAppStatus.description === StatusOptions.SOFTPHONE_CONNECTION_REJECTED;

    if (!isCurrentTabOffline && !data?.force) {
      this.isOtherTabActive = false;
      this.otherTabState = null;
      return;
    }

    if (!data) {
      this.isOtherTabActive = false;
      this.otherTabState = null;
      this.currentPhoneStatus = 'offline';
      this.resolveButtonLabel();
      BroadcastMediatorService.notify('phoneApp', {
        otherTabPhoneStatus: 'offline',
      });
      return;
    }

    const otherTabPhoneStatus = data.phoneStatus;

    const isOtherTabOnline = otherTabPhoneStatus === 'online' ||
                            otherTabPhoneStatus === 'loading';
    const isOtherTabPause = otherTabPhoneStatus === 'pause';
    const isOtherTabBusy = otherTabPhoneStatus === 'ringing' ||
                          otherTabPhoneStatus === 'talking';

    if (isOtherTabBusy) {
      this.isOtherTabActive = true;
      this.otherTabState = data;
      this.currentPhoneStatus = 'other_tab_busy';
      this.resolveButtonLabel();
    } else if (isOtherTabOnline || isOtherTabPause) {
      this.isOtherTabActive = true;
      this.otherTabState = data;
      this.currentPhoneStatus = 'other_tab_online';
      this.resolveButtonLabel();
    } else {
      this.isOtherTabActive = false;
      this.currentPhoneStatus = 'offline';
      this.otherTabState = null;
      this.resolveButtonLabel();
    }

    BroadcastMediatorService.notify('phoneApp', {
      otherTabPhoneStatus: this.currentPhoneStatus,
    });
  }

  private setupPostMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      if (event.data && event.data.type === 'REQUEST_PHONE_STATUS_CONFIRMATION') {
        this.showPhoneStatusConfirmationModal();
      }
    };
    window.addEventListener('message', this.messageListener);
  }

  private removePostMessageListener(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = undefined;
    }
  }

  private showPhoneStatusConfirmationModal(): void {
    const modalRef = this.modalService.open(PhoneStatusConfirmationModalComponent, {
      maxWidth: 480,
    });

    modalRef.afterClose.subscribe((result: { confirmed: boolean } | undefined) => {
      const confirmed = result?.confirmed ?? false;
      try {
        if (confirmed) {
          this.loggerService.log(LogEvent.LIVECHAT_PHONE_STATUS_CONFIRMATION_MODAL_CONFIRMED, {
            currentPhoneStatus: this.currentPhoneStatus,
            otherTabActive: this.isOtherTabActive,
          });
        } else {
          this.loggerService.log(LogEvent.LIVECHAT_PHONE_STATUS_CONFIRMATION_MODAL_CANCELED, {
            currentPhoneStatus: this.currentPhoneStatus,
            otherTabActive: this.isOtherTabActive,
          });
        }
      } catch (error) {
        console.error(error);
      }

      const dashboardIframe = document.querySelector('#phoneApp iframe') as HTMLIFrameElement;
      if (dashboardIframe && dashboardIframe.contentWindow) {
        dashboardIframe.contentWindow.postMessage(
          {
            type: 'PHONE_STATUS_CONFIRMATION_RESPONSE',
            confirmed,
          },
          '*'
        );
      }
    });
  }
}
